'use client';

import { useCallback, useEffect, useRef } from 'react';
import { httpClientPostJson } from '../httpClient';
import type { LegacyBaseTrackingEvent } from './legacy-types';

const BATCH_IDLE_TIMEOUT = 2000; // 2 seconds

export const useCreateLogStash = () => {
  const queueRef = useRef<LegacyBaseTrackingEvent['data'][]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const logStashUrl = process.env.NEXT_PUBLIC_LOG_STASH;

  const sendBatch = useCallback(async () => {
    if (queueRef.current.length === 0 || !logStashUrl) return;

    const batch = [...queueRef.current];
    queueRef.current = [];

    try {
      await httpClientPostJson(logStashUrl, batch);

      if (process.env.NODE_ENV === 'development') {
        // biome-ignore lint/suspicious/noConsole: allow console logging in dev
        console.log('LogStash batch sent:', batch.length, 'events');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to send LogStash batch:', error);
      }
    }
  }, [logStashUrl]);

  const addToQueue = useCallback(
    (eventData: LegacyBaseTrackingEvent['data']) => {
      if (!logStashUrl) return;

      queueRef.current.push(eventData);

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout for idle batch send
      timeoutRef.current = setTimeout(() => {
        sendBatch();
      }, BATCH_IDLE_TIMEOUT);
    },
    [logStashUrl, sendBatch],
  );

  // Cleanup on unmount: send remaining events
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Send any remaining events synchronously if possible
      if (queueRef.current.length > 0 && logStashUrl) {
        // Use sendBeacon for reliability during page unload
        if (navigator?.sendBeacon) {
          navigator.sendBeacon(logStashUrl, JSON.stringify(queueRef.current));
        }
      }
    };
  }, [logStashUrl]);

  return { addToQueue };
};
